import { Phone, DollarSign, AlertTriangle, HelpCircle, CheckCircle, Calendar } from 'lucide-react'
import type { LeadQualityStats } from '../types'
import { cn } from '@/lib/utils'

interface KpiCardsRowProps {
  stats: LeadQualityStats
}

interface KpiCardProps {
  title: string
  icon: React.ElementType
  value: number | string
  subtitle: string
  iconColor?: string
  valueColor?: string
}

function KpiCard({ title, icon: Icon, value, subtitle, iconColor = 'text-gray-400', valueColor = 'text-gray-900' }: KpiCardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <Icon className={cn('w-5 h-5', iconColor)} />
      </div>
      <p className={cn('text-3xl font-bold', valueColor)}>{value}</p>
      <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
    </div>
  )
}

export function KpiCardsRow({ stats }: KpiCardsRowProps) {
  const paidPct = stats.total_calls > 0
    ? ((stats.paid_calls / stats.total_calls) * 100).toFixed(1)
    : '0.0'

  return (
    <div className="grid grid-cols-6 gap-4">
      {/* Total Calls */}
      <KpiCard
        title="Total Calls"
        icon={Phone}
        value={stats.total_calls}
        subtitle="All calls in period"
        iconColor="text-blue-500"
      />

      {/* Paid Calls (GCLID) */}
      <KpiCard
        title="Paid Calls (GCLID)"
        icon={DollarSign}
        value={stats.paid_calls}
        subtitle={`${paidPct}% of total volume`}
        iconColor="text-green-500"
      />

      {/* Spam + Irrelevant */}
      <KpiCard
        title="Spam + Irrelevant"
        icon={AlertTriangle}
        value={stats.irrelevant_calls}
        subtitle={`${stats.irrelevant_rate}% irrelevant rate`}
        iconColor="text-red-500"
        valueColor="text-red-600"
      />

      {/* Unclear Calls */}
      <KpiCard
        title="Unclear Calls"
        icon={HelpCircle}
        value={stats.unclear_calls}
        subtitle={`${stats.paid_calls > 0 ? ((stats.unclear_calls / stats.paid_calls) * 100).toFixed(1) : '0.0'}% low quality`}
        iconColor="text-yellow-500"
        valueColor="text-yellow-600"
      />

      {/* Relevant Calls */}
      <KpiCard
        title="Relevant Calls"
        icon={CheckCircle}
        value={stats.relevant_calls}
        subtitle={`${stats.relevant_rate}% Relevant rate`}
        iconColor="text-emerald-500"
        valueColor="text-emerald-600"
      />

      {/* Booked Appointments */}
      <KpiCard
        title="Booked Appointments"
        icon={Calendar}
        value={stats.booked_calls}
        subtitle={`${stats.booking_rate}% booking rate`}
        iconColor="text-purple-500"
        valueColor="text-purple-600"
      />
    </div>
  )
}
